import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Tooltip,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Close as CloseIcon,
  Star as StarIcon,
  Link as LinkIcon,
  DeleteOutline as DeleteIcon,
  FolderOutlined as FolderIcon,
  ContentCopy as CopyIcon,
  LocalOfferOutlined as TagIcon,
  History as HistoryIcon,
  Lock as LockIcon,
  OpenInNew as OpenInNewIcon,
  Preview as PreviewIcon,
  Download as DownloadIcon,
  CheckCircleOutline as CheckInIcon,
  DeleteSweep as DiscardIcon,
} from '@mui/icons-material';
import cfosService, { CfosApiError } from '../../services/cfosService';
import FileTypeIcon from './FileTypeIcon';
import {
  isOfficeWebEditableSize,
  isOfficeWebFile,
  isPdfFile,
  officeAppLabel,
} from '../../lib/cfos/fileKind';
import { commonTags, filterTagSuggestions, uniqueTags } from '../../lib/cfos/tags';
import type { FileRecord, User } from '../../lib/cfos/types';
import { ROLE } from '../../utils/roles';

export interface DetailsPanelProps {
  file: FileRecord;
  files: FileRecord[];
  selectedIds: Set<string>;
  knownTags: string[];
  user: User;
  sandboxId: string;
  onClose: () => void;
  refresh: () => void;
  notify: (message: string, severity?: 'success' | 'error' | 'warning' | 'info') => void;
  panelBorder: string;
  onOpenOffice: (file: FileRecord, opts?: { preview?: boolean; edit?: boolean }) => void;
  onFileReplaced?: (file: FileRecord) => void;
}

function formatSize(value: number) {
  return value < 1024 * 1024
    ? `${Math.max(1, Math.round(value / 1024))} KB`
    : `${(value / 1024 / 1024).toFixed(1)} MB`;
}

function formatWhen(iso: string) {
  return new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

function highlightTagMatch(tag: string, query: string, accent: string) {
  const needle = query.trim();
  if (!needle) return tag;
  const index = tag.toLocaleLowerCase().indexOf(needle.toLocaleLowerCase());
  if (index < 0) return tag;
  return (
    <>
      {tag.slice(0, index)}
      <Box component="span" sx={{ color: accent, fontWeight: 700 }}>
        {tag.slice(index, index + needle.length)}
      </Box>
      {tag.slice(index + needle.length)}
    </>
  );
}

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    component="h3"
    sx={{
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: 'text.secondary',
      mb: 1,
    }}
  >
    {children}
  </Typography>
);

const OutlineAction: React.FC<{
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  border: string;
}> = ({ icon, label, onClick, disabled, border }) => (
  <Button
    fullWidth
    variant="outlined"
    disabled={disabled}
    onClick={onClick}
    startIcon={icon}
    sx={{
      justifyContent: 'center',
      py: 1.1,
      borderColor: border,
      color: 'text.primary',
      fontWeight: 600,
      textTransform: 'none',
      borderRadius: 1.5,
      bgcolor: 'transparent',
      '&:hover': {
        borderColor: 'primary.main',
        bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.08 : 0.04),
      },
    }}
  >
    {label}
  </Button>
);

const DetailsPanel: React.FC<DetailsPanelProps> = ({
  file,
  files,
  selectedIds,
  knownTags,
  user,
  sandboxId,
  onClose,
  refresh,
  notify,
  panelBorder,
  onOpenOffice,
  onFileReplaced,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const sand = '#ccba9b';
  const controlBg = isDark ? alpha('#000', 0.25) : alpha('#0f2134', 0.03);
  const iconWellBg = isDark ? alpha(sand, 0.12) : alpha('#E53935', 0.08);

  const [tagInput, setTagInput] = useState('');
  const [sandboxTags, setSandboxTags] = useState<string[]>(knownTags);
  const [tagHighlight, setTagHighlight] = useState(-1);
  const [revisionEditor, setRevisionEditor] = useState(false);
  const [targetQuery, setTargetQuery] = useState('');
  const [targetId, setTargetId] = useState('');
  const [revisionNumber, setRevisionNumber] = useState(String(file.revision_number ?? ''));
  const [targetRevisionNumber, setTargetRevisionNumber] = useState('');
  const [revisionError, setRevisionError] = useState('');
  const [revisionSaving, setRevisionSaving] = useState(false);
  const [sameFileEditor, setSameFileEditor] = useState(false);
  const [sameFileQuery, setSameFileQuery] = useState('');
  const [sameFileTargetId, setSameFileTargetId] = useState('');
  const [sameFileError, setSameFileError] = useState('');
  const [sameFileSaving, setSameFileSaving] = useState(false);
  const [sameFileMembers, setSameFileMembers] = useState<FileRecord[]>([]);
  const [sameFileLoading, setSameFileLoading] = useState(false);
  const [newPath, setNewPath] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => setRevisionNumber(String(file.revision_number ?? '')), [file.id, file.revision_number]);
  useEffect(() => {
    setSameFileEditor(false);
    setSameFileQuery('');
    setSameFileTargetId('');
    setSameFileError('');
    setRevisionEditor(false);
  }, [file.id]);

  useEffect(() => {
    if (!file.same_file_group_id || !isPdfFile(file)) {
      setSameFileMembers([]);
      return;
    }
    setSameFileLoading(true);
    cfosService.sameFiles(file.id)
      .then(setSameFileMembers)
      .catch(() => setSameFileMembers([]))
      .finally(() => setSameFileLoading(false));
  }, [file.id, file.same_file_group_id]);

  useEffect(() => {
    if (!sandboxId) return;
    let cancelled = false;
    cfosService.listTags(undefined, sandboxId)
      .then((tags) => {
        if (!cancelled) setSandboxTags(tags);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [sandboxId]);

  const pdfOnly = isPdfFile(file);
  const targetIds = selectedIds.size > 1 ? [...selectedIds] : [file.id];
  const targetFiles = files.filter((item) => targetIds.includes(item.id));
  const assignedTags = uniqueTags(targetFiles);
  const sharedTags = commonTags(targetFiles);
  const bulk = targetIds.length > 1;
  const officeWeb = !bulk && isOfficeWebFile(file);
  const officeOversize = officeWeb && !isOfficeWebEditableSize(file);
  const officeSession = file.office_session ?? null;
  const canManageOffice = Boolean(
    officeSession
    && (
      String(officeSession.owner_user_id) === String(user.id)
      || user.user_type === ROLE.ADMIN
    ),
  );

  const candidates = useMemo(
    () => files
      .filter((item) => isPdfFile(item) && item.id !== file.id && item.filename.toLowerCase().includes(targetQuery.toLowerCase()))
      .slice(0, 8),
    [files, file.id, targetQuery],
  );

  const sameFileCandidates = useMemo(
    () => files
      .filter((item) => {
        if (!isPdfFile(item) || item.id === file.id) return false;
        if (file.same_file_group_id && item.same_file_group_id === file.same_file_group_id) return false;
        return item.filename.toLowerCase().includes(sameFileQuery.toLowerCase());
      })
      .slice(0, 8),
    [files, file.id, file.same_file_group_id, sameFileQuery],
  );

  const pooledTags = useMemo(
    () => uniqueTags([{ tags: knownTags }, { tags: sandboxTags }, ...files]),
    [knownTags, sandboxTags, files],
  );
  const tagSuggestions = useMemo(() => {
    if (!tagInput.trim()) return [];
    const matching = filterTagSuggestions(pooledTags, tagInput, assignedTags, 12);
    return matching.length ? matching : filterTagSuggestions(pooledTags, '', assignedTags, 8);
  }, [pooledTags, tagInput, assignedTags]);

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 1.5,
      bgcolor: controlBg,
      '& fieldset': { borderColor: panelBorder },
      '&:hover fieldset': { borderColor: isDark ? alpha(sand, 0.45) : alpha('#0f2134', 0.28) },
      '&.Mui-focused fieldset': { borderColor: sand },
    },
  } as const;

  const run = async (operation: () => Promise<unknown>, message: string) => {
    setBusy(true);
    try {
      await operation();
      if (message) notify(message, 'success');
      refresh();
    } catch (error) {
      notify((error as Error).message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const addTagInFlight = useRef<string | null>(null);
  const addTagToSelection = async (tag: string) => {
    const next = tag.trim();
    if (!next || addTagInFlight.current === next) return;
    addTagInFlight.current = next;
    try {
      await cfosService.addTags(targetIds, next, sandboxId);
      notify(bulk ? `Tag added to ${targetIds.length} files.` : 'Tag added.', 'success');
      setSandboxTags((tags) => (tags.includes(next) ? tags : [...tags, next]));
      setTagInput('');
      setTagHighlight(-1);
      refresh();
    } catch (error) {
      notify((error as Error).message, 'error');
    } finally {
      addTagInFlight.current = null;
    }
  };

  const removeTagFromSelection = (tag: string) => run(async () => {
    const removals = targetFiles
      .filter((item) => item.tag_ids[tag])
      .map((item) => cfosService.removeTag(item.id, item.tag_ids[tag]));
    if (removals.length) await Promise.all(removals);
  }, bulk ? `Tag removed from ${targetIds.length} files.` : 'Tag removed.');

  const copyPath = async (path: string) => {
    try {
      await navigator.clipboard.writeText(path);
      notify('Path copied.', 'success');
    } catch {
      notify('Could not copy path.', 'error');
    }
  };

  const validRevision = (value: string) => value !== '' && Number.isInteger(Number(value)) && Number(value) >= 0;

  const assignRevision = async () => {
    const target = files.find((item) => item.id === targetId);
    if (!target || !validRevision(revisionNumber) || (!target.engineering_document_id && !validRevision(targetRevisionNumber))) {
      setRevisionError('Select a target and enter non-negative whole revision numbers.');
      return;
    }
    setRevisionSaving(true);
    setRevisionError('');
    try {
      await cfosService.assignRevision(target.id, {
        file_id: file.id,
        revision_number: Number(revisionNumber),
        ...(!target.engineering_document_id ? { target_revision_number: Number(targetRevisionNumber) } : {}),
      });
      notify(`${file.filename} assigned as revision ${revisionNumber}.`, 'success');
      setRevisionEditor(false);
      refresh();
    } catch (error) {
      setRevisionError((error as Error).message);
    } finally {
      setRevisionSaving(false);
    }
  };

  const renumberRevision = async () => {
    if (!validRevision(revisionNumber)) {
      setRevisionError('Enter a non-negative whole revision number.');
      return;
    }
    setRevisionSaving(true);
    setRevisionError('');
    try {
      await cfosService.renumberRevision(file.id, Number(revisionNumber));
      notify(`Revision changed to ${revisionNumber}.`, 'success');
      refresh();
    } catch (error) {
      setRevisionError((error as Error).message);
    } finally {
      setRevisionSaving(false);
    }
  };

  const linkSameFile = async () => {
    const sameFileTarget = files.find((item) => item.id === sameFileTargetId);
    if (!sameFileTarget) {
      setSameFileError('Select another file to mark as the same.');
      return;
    }
    setSameFileSaving(true);
    setSameFileError('');
    try {
      await cfosService.linkSameFiles([file.id, sameFileTarget.id]);
      notify(`${file.filename} and ${sameFileTarget.filename} marked as the same file.`, 'success');
      setSameFileEditor(false);
      refresh();
    } catch (error) {
      setSameFileError((error as Error).message);
    } finally {
      setSameFileSaving(false);
    }
  };

  return (
    <Box
      sx={{
        width: { xs: '100%', lg: 340 },
        borderLeft: 1,
        borderColor: panelBorder,
        display: 'flex',
        flexDirection: 'column',
        maxHeight: '100%',
        bgcolor: 'background.paper',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.25,
          borderBottom: 1,
          borderColor: panelBorder,
        }}
      >
        <Typography variant="subtitle2" fontWeight={700} letterSpacing="-0.01em">
          File details
        </Typography>
        <IconButton size="small" onClick={onClose} aria-label="Close details">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 2 }}>
        <Stack spacing={3}>
          {/* Identity */}
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 1.5,
                display: 'grid',
                placeItems: 'center',
                bgcolor: iconWellBg,
                border: 1,
                borderColor: panelBorder,
                flexShrink: 0,
              }}
            >
              <FileTypeIcon filename={bulk ? 'selection.pdf' : file.filename} fontSize="medium" />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="body2"
                fontWeight={700}
                sx={{
                  color: 'text.primary',
                  letterSpacing: '-0.01em',
                  lineHeight: 1.35,
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {bulk ? `${targetIds.length} files selected` : file.filename}
              </Typography>
              {!bulk && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {formatSize(file.file_size)} · {formatWhen(file.updated_at || file.created_at)}
                </Typography>
              )}
            </Box>
          </Stack>

          {officeWeb && (
            <Box>
              <SectionLabel>Microsoft 365</SectionLabel>
              <Stack spacing={1}>
                {officeSession?.status === 'checked_out' && (
                  <Alert severity="warning" icon={<LockIcon fontSize="inherit" />} sx={{ py: 0 }}>
                    Checked out by{' '}
                    {officeSession.owner
                      ? `${officeSession.owner.name} ${officeSession.owner.last_name}`.trim()
                      : 'another user'}
                    {officeSession.expires_at
                      ? ` · expires ${formatWhen(officeSession.expires_at)}`
                      : ''}
                  </Alert>
                )}
                {officeOversize && (
                  <Alert severity="info" sx={{ py: 0 }}>
                    Over {Math.round(100)} MB — Microsoft 365 web edit unavailable. Download instead.
                  </Alert>
                )}
                {!officeOversize && (
                  <>
                    <OutlineAction
                      icon={<OpenInNewIcon fontSize="small" />}
                      label={`Edit in ${officeAppLabel(file)}`}
                      border={panelBorder}
                      disabled={busy}
                      onClick={() => onOpenOffice(file, { edit: true })}
                    />
                    <OutlineAction
                      icon={<PreviewIcon fontSize="small" />}
                      label="Preview"
                      border={panelBorder}
                      disabled={busy}
                      onClick={() => onOpenOffice(file, { preview: true })}
                    />
                  </>
                )}
                {officeSession?.status === 'checked_out' && canManageOffice && (
                  <>
                    <OutlineAction
                      icon={<CheckInIcon fontSize="small" />}
                      label="Check in"
                      border={panelBorder}
                      disabled={busy}
                      onClick={() => void run(async () => {
                        const result = await cfosService.checkinOffice(file.id);
                        onFileReplaced?.(result.file);
                        const messages = {
                          unchanged: 'No changes — working copy discarded',
                          linked: 'Checked in — linked to existing content',
                          replaced: 'Checked in — new CFOS version created',
                        } as const;
                        notify(messages[result.action], 'success');
                      }, '')}
                    />
                    <OutlineAction
                      icon={<DiscardIcon fontSize="small" />}
                      label="Discard checkout"
                      border={panelBorder}
                      disabled={busy}
                      onClick={() => void run(() => cfosService.discardOffice(file.id), 'Working copy discarded')}
                    />
                  </>
                )}
                <OutlineAction
                  icon={<DownloadIcon fontSize="small" />}
                  label="Download"
                  border={panelBorder}
                  disabled={busy}
                  onClick={() => {
                    void (async () => {
                      try {
                        const blob = await cfosService.fileContentBlob(file.id);
                        const url = URL.createObjectURL(blob);
                        const anchor = document.createElement('a');
                        anchor.href = url;
                        anchor.download = file.filename;
                        document.body.appendChild(anchor);
                        anchor.click();
                        anchor.remove();
                        URL.revokeObjectURL(url);
                      } catch (err) {
                        notify(
                          err instanceof CfosApiError ? err.message : (err as Error).message,
                          'error',
                        );
                      }
                    })();
                  }}
                />
              </Stack>
            </Box>
          )}

          {/* Locations */}
          <Box>
            <SectionLabel>Locations</SectionLabel>
            <Stack spacing={1}>
              {file.paths.map((location) => (
                <Box
                  key={location.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                    px: 1.25,
                    py: 1,
                    border: 1,
                    borderColor: panelBorder,
                    borderRadius: 1.5,
                    bgcolor: controlBg,
                  }}
                >
                  <FolderIcon sx={{ fontSize: 18, color: 'text.secondary', mt: 0.25 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} noWrap title={location.path}>
                      {location.path}
                    </Typography>
                    {location.sandbox_name && (
                      <Typography variant="caption" color="text.secondary">
                        {location.sandbox_name}
                      </Typography>
                    )}
                  </Box>
                  <Tooltip title="Copy path">
                    <IconButton size="small" aria-label="Copy path" onClick={() => void copyPath(location.path)}>
                      <CopyIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                  {file.paths.length > 1 && (
                    <Tooltip title="Remove location">
                      <IconButton
                        size="small"
                        aria-label="Remove location"
                        disabled={busy}
                        onClick={() => run(
                          () => cfosService.deletePath(file.id, location.id),
                          'Location removed.',
                        )}
                      >
                        <DeleteIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              ))}
            </Stack>

            <Stack direction="row" spacing={1} sx={{ mt: 1.25 }}>
              <TextField
                size="small"
                placeholder="/folder"
                value={newPath}
                onChange={(e) => setNewPath(e.target.value)}
                sx={{ flex: 1, ...fieldSx }}
              />
              <Button
                size="small"
                variant="outlined"
                disabled={!newPath.trim() || busy}
                onClick={() => run(async () => {
                  await cfosService.addPath(file.id, newPath.trim(), sandboxId);
                  setNewPath('');
                }, 'Location added.')}
                sx={{
                  minWidth: 56,
                  borderColor: panelBorder,
                  color: 'text.primary',
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 1.5,
                }}
              >
                Add
              </Button>
            </Stack>
          </Box>

          {/* Tags */}
          <Box>
            <SectionLabel>Tags</SectionLabel>
            {assignedTags.length > 0 ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.25 }}>
                {assignedTags.map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    size="small"
                    onDelete={() => removeTagFromSelection(tag)}
                    variant={sharedTags.includes(tag) ? 'filled' : 'outlined'}
                    sx={{
                      borderRadius: 1,
                      borderColor: panelBorder,
                      ...(sharedTags.includes(tag)
                        ? {
                            bgcolor: isDark ? alpha(sand, 0.16) : alpha('#0f2134', 0.06),
                            color: 'text.primary',
                          }
                        : {}),
                    }}
                  />
                ))}
              </Box>
            ) : (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                No tags
              </Typography>
            )}
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Add a tag"
                  value={tagInput}
                  onChange={(e) => {
                    setTagInput(e.target.value);
                    setTagHighlight(-1);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowDown' && tagSuggestions.length) {
                      e.preventDefault();
                      setTagHighlight((index) => Math.min(index < 0 ? 0 : index + 1, tagSuggestions.length - 1));
                      return;
                    }
                    if (e.key === 'ArrowUp' && tagSuggestions.length) {
                      e.preventDefault();
                      setTagHighlight((index) => Math.max(index - 1, -1));
                      return;
                    }
                    if (e.key === 'Enter' && tagInput.trim()) {
                      e.preventDefault();
                      const picked = tagHighlight >= 0 ? tagSuggestions[tagHighlight] : tagInput.trim();
                      if (picked) void addTagToSelection(picked);
                    }
                  }}
                  sx={fieldSx}
                />
                {tagSuggestions.length > 0 && (
                  <Box
                    role="listbox"
                    aria-label="Existing tags"
                    sx={{
                      mt: 0.75,
                      maxHeight: 220,
                      overflow: 'auto',
                      border: 1,
                      borderColor: panelBorder,
                      borderRadius: 1.5,
                      bgcolor: isDark ? '#152840' : '#ffffff',
                    }}
                  >
                    {tagSuggestions.map((tag, index) => (
                      <Box
                        key={tag}
                        role="option"
                        aria-selected={index === tagHighlight}
                        onMouseDown={(event) => {
                          event.preventDefault();
                          void addTagToSelection(tag);
                        }}
                        onMouseEnter={() => setTagHighlight(index)}
                        sx={{
                          px: 1.25,
                          py: 0.75,
                          fontSize: 13,
                          cursor: 'pointer',
                          bgcolor: index === tagHighlight
                            ? (isDark ? alpha(sand, 0.16) : alpha('#0f2134', 0.06))
                            : 'transparent',
                        }}
                      >
                        {highlightTagMatch(tag, tagInput, sand)}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
              <IconButton
                aria-label="Add tag"
                disabled={!tagInput.trim()}
                onClick={() => tagInput.trim() && void addTagToSelection(tagInput.trim())}
                sx={{
                  border: 1,
                  borderColor: panelBorder,
                  borderRadius: 1.5,
                  width: 40,
                  height: 40,
                }}
              >
                <TagIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Box>

          {pdfOnly && (
            <>
              {/* Same file */}
              <Box>
                <SectionLabel>
                  OTHER COPIES
                  {sameFileLoading && <CircularProgress size={12} sx={{ ml: 1 }} />}
                </SectionLabel>
                {sameFileMembers.filter((m) => m.id !== file.id).map((member) => (
                  <Box
                    key={member.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1,
                      px: 1.25,
                      py: 1,
                      border: 1,
                      borderColor: panelBorder,
                      borderRadius: 1.5,
                      bgcolor: controlBg,
                    }}
                  >
                    <LinkIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" noWrap sx={{ flex: 1 }}>{member.filename}</Typography>
                  </Box>
                ))}
                {!sameFileEditor ? (
                  <Stack spacing={1}>
                    <OutlineAction
                      icon={<LinkIcon />}
                      label="Link to existing file"
                      border={panelBorder}
                      onClick={() => setSameFileEditor(true)}
                    />
                    {file.same_file_group_id && (
                      <Button
                        size="small"
                        color="warning"
                        onClick={() => run(() => cfosService.unlinkSameFile(file.id), 'Unlinked from same-file group.')}
                        sx={{ textTransform: 'none' }}
                      >
                        Unlink this copy
                      </Button>
                    )}
                  </Stack>
                ) : (
                  <Stack spacing={1}>
                    <TextField
                      size="small"
                      placeholder="Search filename…"
                      value={sameFileQuery}
                      onChange={(e) => { setSameFileQuery(e.target.value); setSameFileTargetId(''); }}
                      sx={fieldSx}
                    />
                    <List
                      dense
                      disablePadding
                      sx={{
                        maxHeight: 128,
                        overflow: 'auto',
                        border: 1,
                        borderColor: panelBorder,
                        borderRadius: 1.5,
                      }}
                    >
                      {sameFileCandidates.map((item) => (
                        <ListItemButton
                          key={item.id}
                          selected={sameFileTargetId === item.id}
                          onClick={() => setSameFileTargetId(item.id)}
                        >
                          <ListItemText
                            primary={item.filename}
                            primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                          />
                        </ListItemButton>
                      ))}
                      {!sameFileCandidates.length && (
                        <Typography variant="caption" color="text.secondary" sx={{ p: 1.25, display: 'block' }}>
                          No matching files.
                        </Typography>
                      )}
                    </List>
                    {sameFileError && <Alert severity="error" sx={{ py: 0 }}>{sameFileError}</Alert>}
                    <Stack direction="row" spacing={1}>
                      <Button size="small" onClick={() => setSameFileEditor(false)} sx={{ textTransform: 'none' }}>
                        Cancel
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        disabled={sameFileSaving}
                        onClick={() => void linkSameFile()}
                        sx={{ textTransform: 'none', bgcolor: isDark ? sand : '#0f2134', color: isDark ? '#0f2134' : '#fff' }}
                      >
                        Link
                      </Button>
                    </Stack>
                  </Stack>
                )}
              </Box>

              {/* Engineering revisions */}
              <Box>
                <SectionLabel>Document Revision</SectionLabel>
                {file.revision_number !== null && (
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
                    <Typography variant="body2" fontWeight={600}>
                      Rev {file.revision_number}
                    </Typography>
                    {file.is_contractual && (
                      <Chip
                        icon={<StarIcon sx={{ fontSize: '14px !important' }} />}
                        label="Contractual"
                        size="small"
                        sx={{
                          height: 22,
                          bgcolor: alpha(sand, isDark ? 0.2 : 0.35),
                          color: 'text.primary',
                          border: 1,
                          borderColor: panelBorder,
                        }}
                      />
                    )}
                  </Stack>
                )}
                {file.engineering_document_id ? (
                  <Stack spacing={1}>
                    <TextField
                      size="small"
                      label="Revision number"
                      value={revisionNumber}
                      onChange={(e) => setRevisionNumber(e.target.value)}
                      sx={fieldSx}
                    />
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={revisionSaving}
                        onClick={() => void renumberRevision()}
                        sx={{ textTransform: 'none', borderColor: panelBorder, color: 'text.primary' }}
                      >
                        Renumber
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => run(() => cfosService.unassignRevision(file.id), 'Removed from revision series.')}
                        sx={{ textTransform: 'none', borderColor: panelBorder, color: 'text.primary' }}
                      >
                        Unassign
                      </Button>
                      {!file.is_contractual ? (
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => run(() => cfosService.setContractualRevision(file.id), 'Set as contractual revision.')}
                          sx={{ textTransform: 'none', bgcolor: isDark ? sand : '#0f2134', color: isDark ? '#0f2134' : '#fff' }}
                        >
                          Set contractual
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          color="warning"
                          onClick={() => run(() => cfosService.clearContractualRevision(file.id), 'Contractual status cleared.')}
                          sx={{ textTransform: 'none' }}
                        >
                          Clear contractual
                        </Button>
                      )}
                    </Stack>
                    {revisionError && <Alert severity="error" sx={{ py: 0 }}>{revisionError}</Alert>}
                  </Stack>
                ) : !revisionEditor ? (
                  <OutlineAction
                    icon={<HistoryIcon />}
                    label="Assign a revision"
                    border={panelBorder}
                    onClick={() => setRevisionEditor(true)}
                  />
                ) : (
                  <Stack spacing={1}>
                    <TextField
                      size="small"
                      placeholder="Search target file…"
                      value={targetQuery}
                      onChange={(e) => { setTargetQuery(e.target.value); setTargetId(''); }}
                      sx={fieldSx}
                    />
                    <List
                      dense
                      disablePadding
                      sx={{
                        maxHeight: 128,
                        overflow: 'auto',
                        border: 1,
                        borderColor: panelBorder,
                        borderRadius: 1.5,
                      }}
                    >
                      {candidates.map((item) => (
                        <ListItemButton key={item.id} selected={targetId === item.id} onClick={() => setTargetId(item.id)}>
                          <ListItemText primary={item.filename} primaryTypographyProps={{ variant: 'body2', noWrap: true }} />
                        </ListItemButton>
                      ))}
                    </List>
                    <TextField
                      size="small"
                      label="This file revision #"
                      value={revisionNumber}
                      onChange={(e) => setRevisionNumber(e.target.value)}
                      sx={fieldSx}
                    />
                    {targetId && !files.find((f) => f.id === targetId)?.engineering_document_id && (
                      <TextField
                        size="small"
                        label="Target revision #"
                        value={targetRevisionNumber}
                        onChange={(e) => setTargetRevisionNumber(e.target.value)}
                        sx={fieldSx}
                      />
                    )}
                    {revisionError && <Alert severity="error" sx={{ py: 0 }}>{revisionError}</Alert>}
                    <Stack direction="row" spacing={1}>
                      <Button size="small" onClick={() => setRevisionEditor(false)} sx={{ textTransform: 'none' }}>
                        Cancel
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        disabled={revisionSaving}
                        onClick={() => void assignRevision()}
                        sx={{ textTransform: 'none', bgcolor: isDark ? sand : '#0f2134', color: isDark ? '#0f2134' : '#fff' }}
                      >
                        Assign
                      </Button>
                    </Stack>
                  </Stack>
                )}
              </Box>
            </>
          )}
        </Stack>
      </Box>
    </Box>
  );
};

export default DetailsPanel;
